import { Component, inject, Inject, OnInit } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { NzFormModule } from 'ng-zorro-antd/form';
import { CrmTranslatePipe } from 'common-module/translate';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { FormsModule } from '@angular/forms';
import { NgForOf, NgIf } from '@angular/common';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import {
  CrmDefaultPaginationToken,
  CrmPaginated,
  CrmPagination,
} from 'common-module/table';
import { CrmMessageService } from 'common-module/message';
import { ProductService } from '@app/core/products/product.service';
import { CommonModalsService } from '@app/core/services/common-modals.service';
import { MediaService } from '@app/core/media/media.service';
import { ReportsService } from '@app/core/services/reports.service';
import { getBaseSearchParams } from '@app/utils/product.utils';
import { CrmTableLoadFilters } from 'common-module/table/model/table-type-data';
import { loadAllPaginated } from '@app/shared/helpers/load.helper';
import { Product } from '@app/core/model/product';
import { CrmSafeAny } from 'common-module/core/types';

enum ExportType {
  table = 'table',
  media = 'media',
}

const getFileNameFromFilters = (filters: any) =>
  Object.values(filters)
    .filter(Boolean)
    .filter((v: CrmSafeAny) => v.length)
    .join('_');

export interface ProductTableListFilters {
  filter: { search: string; rdpStatus: string[]; commercialYear: string };
}

enum MediaType {
  draft = 'DRAFT',
  '3D' = '3D',
  '2D' = '2D',
  'TU' = 'TU',
  'CU' = 'CU',
  planogram = 'PLANOGRAM',
}

export const validateStream =
  <T>(validator: (_: any) => boolean, error: string) =>
  (source: Observable<T>) =>
    source.pipe(
      mergeMap((data: T) => {
        if (!validator(data)) {
          return throwError(() => new Error(error));
        }
        return of(data);
      }),
    );

@Component({
  selector: 'app-export-products',
  templateUrl: 'export-products.component.html',
  styleUrls: ['export-products.component.less'],
  standalone: true,
  imports: [
    NzFormModule,
    CrmTranslatePipe,
    NzRadioModule,
    FormsModule,
    NgIf,
    NzDividerModule,
    NgForOf,
    NzCheckboxModule,
    ButtonComponent,
  ],
})
export class ExportProductsComponent implements OnInit {
  public listFilters: CrmTableLoadFilters;
  public exportType: string = ExportType.table;
  public ExportType = ExportType;
  public exportOptions: Record<string, boolean> = {
    [MediaType.draft]: true,
    [MediaType['3D']]: true,
    [MediaType['2D']]: true,
    [MediaType.TU]: true,
    [MediaType.CU]: true,
    [MediaType.planogram]: true,
  };
  public exportProcessing: boolean;
  public exportMediaTypes = [
    { value: MediaType.draft, label: 'modal.export.mediaOptions.drafts' },
    { value: MediaType['3D'], label: 'modal.export.mediaOptions.3d' },
    { value: MediaType['2D'], label: 'modal.export.mediaOptions.2d' },
    { value: MediaType.TU, label: 'modal.export.mediaOptions.tu' },
    { value: MediaType.CU, label: 'modal.export.mediaOptions.cu' },
    {
      value: MediaType.planogram,
      label: 'modal.export.mediaOptions.planogram',
    },
  ];
  private modalData = inject(NZ_MODAL_DATA);

  constructor(
    @Inject(CrmDefaultPaginationToken)
    private DEFAULT_PAGINATION: CrmPagination,
    private messageService: CrmMessageService,
    private reportsService: ReportsService,
    private productService: ProductService,
    private modalService: CommonModalsService,
    private mediaService: MediaService,
    private modalSelfReference: NzModalRef,
  ) {}

  ngOnInit() {
    if (!this.modalData.listFilters) {
      throw new Error('Missing list filters');
    }
    this.listFilters = this.modalData.listFilters;
  }

  public onDownload() {
    if (this.exportProcessing) {
      return;
    }

    if (this.hasError()) {
      this.messageService.error('modal.export.error.invalidData');
      return;
    }

    switch (this.exportType) {
      case ExportType.table:
        this.onProductExport();
        break;
      case ExportType.media:
        this.onMediaExport();
        break;
      default:
        break;
    }
  }

  public hasError() {
    return Object.values(this.exportOptions).every((val) => val === false);
  }

  private onProductExport() {
    if (this.exportProcessing) {
      return;
    }
    this.exportProducts().subscribe({
      next: () => {
        this.messageService.success('modal.export.messages.productsExported');
        this.exportProcessing = true;
      },
      error: (err: Error) => {
        this.messageService.error(err.message);
        this.exportProcessing = false;
      },
    });
  }

  private onMediaExport() {
    const tags = Object.entries(this.exportOptions)
      .filter(([, value]) => value === true)
      .map(([key]) => key);

    if (this.exportProcessing) {
      return;
    }
    this.exportMedia(tags).subscribe({
      next: () => {
        this.messageService.success('modal.export.messages.mediaExported');
        this.exportProcessing = true;
      },
      error: (err: any) => {
        this.messageService.error(err.message);
        this.exportProcessing = false;
      },
    });
  }

  private exportProducts() {
    this.exportProcessing = true;

    const loadProducts$ = (pag: CrmPagination, params?: any) => {
      return this.loadProductIds({
        filters: params,
        pagination: pag,
      });
    };

    return loadAllPaginated(loadProducts$, {
      pageSize: 50,
      params: this.listFilters,
    }).pipe(
      map((data: Product[]) => data.map(({ _id }) => _id)),
      mergeMap((productIds: string[]) =>
        this.reportsService.exportProducts(productIds).pipe(
          catchError((err) => {
            console.error(err);
            return throwError(
              () => new Error('modal.export.error.errorExportingProducts'),
            );
          }),
        ),
      ),
      tap((exportData: any) => {
        const fileName = getFileNameFromFilters(this.listFilters.filter ?? {});
        this.exportProcessing = false;
        this.modalSelfReference.close();
        this.modalService.downloadModal(
          exportData,
          fileName,
          'xlsx',
          () => (this.exportProcessing = false),
        );
      }),
      catchError((err) => {
        console.error(err);
        return throwError(
          () => new Error('modal.export.error.errorExportingProducts'),
        );
      }),
    );
  }

  private exportMedia(tags: string[]) {
    this.exportProcessing = true;

    const loadProducts$ = (pag: CrmPagination, params?: any) => {
      return this.loadProductIds({
        filters: params,
        pagination: pag,
      });
    };

    return loadAllPaginated(loadProducts$, {
      pageSize: 50,
      params: this.listFilters,
    }).pipe(
      validateStream(
        (productIds) => !!productIds,
        'modal.export.error.errorLoadingData',
      ),
      validateStream(
        (productIds) => productIds.length > 0,
        'modal.export.error.noData',
      ),
      map((data: Product[]) => data.map(({ _id }) => _id)),
      mergeMap((productIds: string[]) =>
        this.mediaService
          .exportMedia(productIds, tags)
          .pipe(
            catchError(() =>
              throwError(
                () => new Error('modal.export.error.errorExportingMedia'),
              ),
            ),
          ),
      ),
      tap((exportData: any) => {
        const fileName = getFileNameFromFilters(this.listFilters.filter);
        this.exportProcessing = false;
        this.modalSelfReference.close();
        this.modalService.downloadModal(
          exportData,
          fileName,
          'zip',
          () => (this.exportProcessing = false),
        );
      }),
    );
  }

  private loadProductIds(param: {
    pagination: { total?: number; limit: number; skip: number };
    filters: any;
  }): Observable<CrmPaginated<Product>> {
    const { filters, pagination } = param;
    const baseParams = getBaseSearchParams(filters, pagination);
    return filters?.filter?.search
      ? this.productService.search(filters?.filter?.search, baseParams)
      : this.productService.listProducts(baseParams);
  }
}
